import React, { useEffect, useState } from "react";
import { Card, Box, Button, Text, Layout, Cell, NumberInput, FormField, Input, TextButton } from "@wix/design-system";
import * as Icons from "@wix/wix-ui-icons-common";
import { showToast } from "@wix/dashboard-sdk";
import Setting from "../../../models/setting";
import Loading from "../../common/Loader";
import Site from "../../../models/site";
import Currency from "../../../models/currency";
import axios from "axios";
import "./inventorySettings.css";

const CurrencySettings: React.FC<any> = ({ context = "settings", onSuccess = () => {} }) => {
  // Real time conversion rate
  const [isFetchingRealTimeConversionRate, setIsFetchingRealTimeConversionRate] = useState(false);
  const [realTimeConversionRate, setRealTimeConversionRate] = useState(null);

  // Validation errors
  const [validationErrors, setValidationErrors] = useState({});

  // Currency settings
  const [faireCurrency, setFaireCurrency] = useState("");
  const [faireValue, setFaireValue] = useState("");
  const [conversionRate, setConversionRate] = useState("");
  const [wixCurrency, setWixCurrency] = useState("");
  const wixValue = 100;

  const [isLoading, setLoading] = useState(false);
  const [isCurrencySyncing, setIsCurrencySyncing] = useState(false);

  // Fetch wix currency
  const fetchCurrencySettings = () => {
    Site.details().then((response: any) => {
      setWixCurrency(response.wix_currency || "");
      setFaireCurrency(response.faire_currency || "");
      setConversionRate(response.currency_conversion_rate || "");
    });
  };

  // Get real time conversion rate
  const fetchRealTimeConversionRate = () => {
    setIsFetchingRealTimeConversionRate(true);
    Currency.realTimeConversionRate(wixCurrency, faireCurrency)
      .then((response: any) => {
        setRealTimeConversionRate(response.data.conversion_rate);
      })
      .finally(() => {
        setIsFetchingRealTimeConversionRate(false);
      });
  };

  useEffect(() => {
    if (realTimeConversionRate) {
      setConversionRate(realTimeConversionRate);
    }
  }, [realTimeConversionRate]);

  useEffect(() => {
    fetchCurrencySettings();
  }, []);

  // Order Setting Submit
  const handleSubmit = () => {
    setLoading(true);

    let data = {
      faire_currency: faireCurrency,
      currency_conversion_rate: conversionRate,
    };

    Setting.setCurrencySettings(data)
      .then((response) => {
        showToast({
          type: "success",
          message: response.message,
        });

        setValidationErrors({});
        onSuccess();
      })
      .catch((error) => {
        showToast({
          type: "error",
          message: error.message,
        });

        setValidationErrors(error.errors);
      })
      .finally(() => {
        setLoading(false);
      });
  };

  useEffect(() => {
    fetchCurrencySettings();
  }, []);

  useEffect(() => {
    if (conversionRate !== "") {
      let newFaireValue = wixValue * conversionRate;
      setFaireValue(newFaireValue);
    }
  }, [conversionRate]);

  // Sync Wix Currency to our application
  const syncWixCurrency = () => {
    setIsCurrencySyncing(true);
    axios
      .get("api/currency/sync-wix-currency")
      .then((response) => {
        setWixCurrency(response?.data);
      })
      .catch((err) => {})
      .finally(() => setIsCurrencySyncing(false));
  };

  return (
    <div>
      <form onSubmit={handleSubmit}>
        <div style={{ marginBottom: "24px" }}>
          <Card>
            {context === "settings" && (
              <>
                <Card.Header title="Currency Settings" />
                <Card.Divider />
                <Box verticalAlign="middle" align="space-between" padding="18px 24px">
                  <Box direction="vertical">
                    <Text weight="bold" skin="standard">
                      Faire currency
                    </Text>
                    <Text secondary size="small">
                      Faire currency based on the store region.
                    </Text>
                  </Box>
                  <Input placeholder="Faire currency" readOnly value={faireCurrency} />
                </Box>
              </>
            )}
            <Box verticalAlign="middle" align="space-between" padding={context === "settings" ? "18px 24px" : ""}>
              <Box direction="vertical">
                <Text weight="bold" skin="standard">
                  Conversion rate
                </Text>
                <Text secondary size="small">
                  Conversion rate between Wix and Faire currency.
                </Text>
              </Box>
              <div style={{ padding: "18px 24px" }} className="cell-wrapper wix-currency">
                <Layout alignItems="center">
                  <Cell span={4}>
                    <FormField
                      label={<Text secondary>Wix currency</Text>}
                      suffix={
                        <TextButton size="small" onClick={syncWixCurrency} disabled={isCurrencySyncing}>
                          Sync Wix Currency
                        </TextButton>
                      }
                    >
                      <Input className="input-with-multiple" value={`${wixCurrency} ${wixValue}`} disabled />
                    </FormField>
                  </Cell>

                  <Cell span={4}>
                    <FormField
                      label="Conversion rate"
                      infoContent="Currency conversion rate"
                      suffix={
                        <TextButton
                          size="small"
                          suffixIcon={<Icons.Refund />}
                          onClick={fetchRealTimeConversionRate}
                          disabled={!wixCurrency || !faireCurrency || isFetchingRealTimeConversionRate}
                        >
                          Use realtime
                        </TextButton>
                      }
                    >
                      <NumberInput value={conversionRate} disabled={wixCurrency === faireCurrency} onChange={(value) => setConversionRate(value)} />
                    </FormField>
                  </Cell>
                  <Cell span={4}>
                    <div>
                      <Text secondary>Faire currency</Text>
                      <Input className="input-with-equal" value={`${faireCurrency} ${faireValue}`} disabled />
                    </div>
                  </Cell>
                </Layout>
              </div>
            </Box>
          </Card>
        </div>

        <Button
          type="button"
          size="medium"
          prefixIcon={isLoading ? <Loading size="tiny" /> : context === "settings" ? <Icons.Download /> : ""}
          suffixIcon={!isLoading && context !== "settings" ? <Icons.ArrowRight /> : ""}
          onClick={handleSubmit}
        >
          {context === "settings" ? "Save configurations" : "Save and Continue"}
        </Button>
      </form>
    </div>
  );
};

export default CurrencySettings;
